import { AfterViewInit, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AppIdentity, UserInfoType } from '@evc/platform';
import { BtnActionEnum, Menu, MenuItemType, ThemeService, ThemeType, ValueOf } from '@evc/web-components';
import { TranslateService } from '@ngx-translate/core';
import { filter, map, Subject, take, takeUntil } from 'rxjs';
import { TenantDetail } from '../_objects/offline-management';
import { ConfigurationItem } from '../_objects/tenant-config';
import { User } from '../_objects/user';
import { OfflineSetupService } from '../_offline/services/offline-setup.service';
import { LoginUserOnlineService } from '../_services/http-api/login-user-online.service';
import { MeUserService } from '../_services/http-api/me-user.service';
import { TenantConfigService } from '../_services/http-api/tenant-config.service';
import { ApplicationMode, DesktopOnline, NetworkStatusService } from '../_services/network-status.service';
import { parseUrlToRoutePage, RoutePage, RouteSettings } from '../app-routes.enum';
import { VERSION } from '../app.constants';
import { TopBarMenuService } from './top-bar-menu.service';
import { NavigationMenuService } from '../shared/services/navigation-menu/navigation-menu.service';
import { isDevelopment } from '../_shared/environment-helper';
import { QRCodeSupportPageComponent } from '../qrcode-support-page/qrcode-support-page.component';
import { EvcModalService } from '../_shared/services/modal/evc-modal.service';
import { LangUpdateService } from '../_services/lang-update.service';

type MenuItemExtended = MenuItemType & { isVisible: boolean };
export const TOPBAR_LAYOUTS = {
  LEGACY: 'legacy',
  PUBLIC: 'public',
  PRIVATE: 'private',
} as const;
export type TopbarLayout = ValueOf<keyof typeof TOPBAR_LAYOUTS>;

@Component({
    selector: 'app-navigation-menu',
    templateUrl: './navigation-menu.component.html',
    styleUrl: './navigation-menu.component.scss',
    standalone: false
})
export class NavigationMenuComponent implements OnDestroy, OnInit, AfterViewInit {
  readonly desktop = DesktopOnline;
  private onDestroy$ = new Subject<void>();
  public applicationMode: ApplicationMode;

  public leftMenu = signal<Menu[]>([]);
  public helpMenu = signal<MenuItemType[]>([]);
  public settingsMenu = signal<MenuItemType[]>([]);

  public userProfile = this.getUserProfile();
  public isLeftbarOpen: boolean = true;
  private tenantConfigs: ConfigurationItem[] = [];
  public topbarLayout: TopbarLayout;
  isOnline = false;

  constructor(
    private networkStatusService: NetworkStatusService,
    private translate: TranslateService,
    private topBarMenuSettingsService: TopBarMenuService,
    private router: Router,
    private meUserService: MeUserService,
    private loginUserService: LoginUserOnlineService,
    private tenantConfigurationService: TenantConfigService,
    private offlineSetupService: OfflineSetupService,
    private navigationMenuService: NavigationMenuService,
    private themeService: ThemeService,
    private evcModalService: EvcModalService,
    private langUpdateService: LangUpdateService,
  ) {
    this.networkStatusService.applicationMode$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((mode) => {
        this.applicationMode = mode;
        this.leftMenu.set(this.buildLeftMenu());
        this.settingsMenu.set(this.buildSettingsMenu());
      });

    this.tenantConfigurationService.getTenantConfigs()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((config) => {
        this.tenantConfigs = config;
      });

    this.tenantConfigurationService.tenantConfig$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(config => {
        this.tenantConfigs = config;
        this.leftMenu.set(this.buildLeftMenu());
      });

    this.helpMenu.set(this.buildHelpMenu());

    this.router.events?.pipe(
      takeUntil(this.onDestroy$),
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.leftMenu.set(this.buildLeftMenu());
      this.hideLeftBarOnInspectionPage(event.url);
    });

    this.langUpdateService.currentLanguageChanged.subscribe(_language => {
      this.leftMenu.set(this.buildLeftMenu());
      this.helpMenu.set(this.buildHelpMenu());
      this.settingsMenu.set(this.buildSettingsMenu());
    });
  }

  ngOnInit(): void {
    this.networkStatusService
      .isOnlineObserver()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(async (isOnline) => {
        this.isOnline = isOnline;
        this.userProfile = this.getUserProfile();
      });

    if (!this.isOnline || this.isOnPremise) {
      this.topbarLayout = TOPBAR_LAYOUTS.PRIVATE;
    }
  }

  private getUserProfile() {
    return this.tenantConfigurationService.getTenantDetails().pipe(
      take(1),
      map((tenant) => this.buildUserProfile(tenant, this.loginUserService.loginUser)
      ));
  }

  ngAfterViewInit(): void {
    this.hideLeftBarOnInspectionPage(this.router.url);
    this.themeService.theme = (localStorage.getItem('app-theme') || 'light') as ThemeType;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.unsubscribe();
  }

  get isDevelopment() {
    return isDevelopment();
  }

  public appIdentity: AppIdentity = {
    logo: './assets/applications/visol.svg#visol',
    title: this.translate.stream('topMenuBar.appTitle'),
    onTitleClick: () => this.router.navigate([RoutePage.IndexPage]),
    copyright: `${this.translate.stream('topMenuBar.version')} ${VERSION} - ${this.translate.stream('topMenuBar.copyrights')}`
  };

  private buildLeftMenu(): Menu[] {
    return this.navigationMenuService.buildLeftMenu(this.networkStatusService.isOnline, this.tenantConfigs);
  }

  private buildHelpMenu(): MenuItemType[] {
    return [
      {
        key: 'support',
        text: this.translate.stream('topMenuBar.support'),
        action: { type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => {
            if (this.isBrianApp) {
              return this.evcModalService.showModal(QRCodeSupportPageComponent);
            }
            return this.topBarMenuSettingsService.goToVisolHelp();
          }
        }
      },
      {
        key: 'legal',
        text: this.translate.stream('topMenuBar.legal'),
        action: { type: BtnActionEnum.CALLBACK, callbackOrUrl: () => this.topBarMenuSettingsService.openLegalDialog() }
      }
    ];
  }

  private hideLeftBarOnInspectionPage(url: string) {
    // We need to hide the prinicipal left bar in the evc-platform element in the inspection page to be able to display a custom one inside the inspection page component
    const element = document.querySelector('#left-bar');
    const route = parseUrlToRoutePage(url);
    const isHiddenDefaultNavBar = route == RoutePage.InspectionDataManagement || route == RoutePage.InspectionReport;

    if (element && isHiddenDefaultNavBar) {
      element.setAttribute('style', 'display: none !important;');
    } else {
      element.setAttribute('style', 'display: flex;');
    }
  }

  private buildSettingsMenu(): MenuItemExtended[] {
    const { applicationMode } = this;

    const isVisolOffline = this.networkStatusService.isVisolOffline(applicationMode);

    if (isVisolOffline) {
      return [];
    }

    const allMenuItems: MenuItemExtended[] = [
      {
        key: 'downloadDesktopApp',
        text: this.translate.stream('topMenuBar.downloadDesktopApp'),
        isVisible: applicationMode.platform != 'desktop',
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.topBarMenuSettingsService.downloadDesktopApp()
        },
      },
      {
        key: 'sharePresetData',
        text: this.translate.stream('topMenuBar.presetData'),
        isVisible: this.isBrianApp,
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.topBarMenuSettingsService.sharePresetDataWithDevice()
        },
      },
      {
        key: 'inspectionTemplate',
        text: this.translate.stream('topMenuBar.inspectionTemplate'),
        isVisible: this.isAdminRole,
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.Settings, RouteSettings.Subject])
        },
      },
      {
        key: 'reportTemplate',
        text: this.translate.stream('topMenuBar.reportTemplate'),
        isVisible: this.isAdminRole,
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.gotoReportTemplateBuilder(),
        },
      },
      {
        key: 'advanced-app-settings',
        text: this.translate.stream('topMenuBar.advancedSettings'),
        isVisible: this.isAdminRole,
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.Settings, RouteSettings.TenantConfig]),
        },
      },
      {
        isVisible: true,
        text: this.translate.stream('topMenuBar.settings.theme.text'),
        key: 'theme',
        collapsible: [
          {
            text: this.translate.stream('topMenuBar.settings.theme.light'),
            key: 'light',
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: (): void => {
                this.themeService.theme = 'light';
                localStorage.setItem('app-theme', 'light');
              },
            },
            img: './assets/theme/light.webp',
          },
          {
            text: this.translate.stream('topMenuBar.settings.theme.dark'),
            key: 'dark',
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: (): void => {
                this.themeService.theme = 'dark';
                localStorage.setItem('app-theme', 'dark');
              }
            },
            img: './assets/theme/dark.webp',
          }
        ],
      }
    ];

    const items = allMenuItems.filter(item => item.isVisible);

    return items;
  }

  private gotoReportTemplateBuilder(): void {
    if (this.applicationMode.platform == 'desktop' && this.applicationMode.mode == 'onPremise') {
      this.router.navigate([RoutePage.Settings, RouteSettings.OfflineReportTemplate]);
    } else {
      window.open(this.meUserService.getReportTemplateBuilderEndpoint(['template-list'], '?application=ViSOL'), '_blank');
    }
  }

  private get isBrianApp() {
    return this.networkStatusService.isBrianMode;
  }

  private get isAdminRole() {
    return this.loginUserService.isTenantAdminRole() || this.applicationMode.platform == 'desktop' && this.applicationMode.mode == 'onPremise';
  }

  private buildUserProfile(tenant: TenantDetail, user: User): UserInfoType {
    return this.networkStatusService.isOnline ?
      {
        id: user?.id,
        tenantId: this.loginUserService.tenantAttribute?.id || tenant?.id,
        displayName: user?.givenName || ' ',
        givenName: user?.givenName,
        surname: user?.surname,
        email: user?.email,
        role: this.loginUserService.tenantAttribute?.role.toLowerCase(),
        telephoneNumber: user?.telephoneNumber,
        organizations: [],
        applications: [],
        color: "#93c6f9"
      }
      : undefined as UserInfoType;
  }

  public resetAppData() {
    if (window.confirm("This will reset application data. Are you sure you want to proceed?")) {
      this.offlineSetupService.resetAppData().subscribe();
    }
  }

  public toggleLeftBar(isLeftbarOpen: boolean) {
    this.isLeftbarOpen = isLeftbarOpen;
    this.navigationMenuService.toggleLeftBar(this.isLeftbarOpen);
  }

  public get isOnPremise() {
    return this.networkStatusService.isOnPremise;
  }

}
